import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Helmet } from 'react-helmet';

import { PAGE_CONTENT_CLASS } from '../../components/layout/PageContent';
import Toast from '../../components/ui/Toast';
import LoadingOverlay from '../../components/ui/LoadingOverlay';
import LoadingMark from '../../components/ui/LoadingMark';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';

import UploadDropZone from './components/UploadDropZone';
import UploadedDocumentFilters from './components/UploadedDocumentFilters';
import UploadedDocumentSortHeader from './components/UploadedDocumentSortHeader';
import UploadedDocumentTableRow from './components/UploadedDocumentTableRow';
import SignatureFieldEditorModal from '../contract-dashboard/components/signature-field-editor/SignatureFieldEditorModal';
import SignatureSendChecklistModal from '../contract-dashboard/components/SignatureSendChecklistModal';
import SignatureCancelConfirmDialog from '../contract-dashboard/components/SignatureCancelConfirmDialog';
import SignatureStatusModal from '../contract-dashboard/components/SignatureStatusModal';
import { normalizeDocumentStatus } from '../../utils/documentStatusMeta';
import UploadedDocumentPdfViewerModal from './components/UploadedDocumentPdfViewerModal';

import { cancelSignatureJob } from '../../services/signatureService';
import { formatApiErrorMessage } from '../../utils/apiErrorMessage';

import contractService from '../../services/contractService';
import AdvancedDocumentSearchModal from '../../components/documents/AdvancedDocumentSearchModal';
import { DOCUMENT_SEARCH_MAX_RESULTS } from '../../utils/documentAdvancedSearch';
import {
  listUploadedSignatureDocumentsPage,
  uploadUploadedSignaturePdfs,
  downloadUploadedSignaturePdf,
  deleteUploadedSignatureDocument,
  MAX_UPLOADED_STORAGE_BYTES,
} from '../../services/uploadedDocumentsService';

const LIST_PAGE_SIZE = 20;

function mergeUploadedDocumentsByJobId(existing, incoming) {
  const byJobId = new Map();
  for (const doc of existing) {
    if (doc?.job_id != null) byJobId.set(String(doc.job_id), doc);
  }
  for (const doc of incoming) {
    if (doc?.job_id != null) byJobId.set(String(doc.job_id), doc);
  }
  return [...byJobId.values()];
}

function fileSizeBytes(doc) {
  const raw = doc?.file_size_bytes ?? doc?.fileSizeBytes;
  const n = Number(raw);
  return Number.isFinite(n) && n >= 0 ? n : 0;
}

function compareUploadedDocuments(a, b, sortKey, sortDir) {
  const mul = sortDir === 'asc' ? 1 : -1;

  if (sortKey === 'name') {
    const va = (a.title || '').toLowerCase();
    const vb = (b.title || '').toLowerCase();
    return mul * va.localeCompare(vb, 'pt-BR');
  }

  if (sortKey === 'size') {
    return mul * (fileSizeBytes(a) - fileSizeBytes(b));
  }

  const da = a.created_at ? new Date(a.created_at).getTime() : 0;
  const db = b.created_at ? new Date(b.created_at).getTime() : 0;
  return db - da;
}

const UploadedDocumentsPage = () => {
  const [documents, setDocuments] = useState([]);
  const [listMode, setListMode] = useState('browse');
  const [searchDocuments, setSearchDocuments] = useState([]);
  const [searchTruncated, setSearchTruncated] = useState(false);
  const [advancedSearchOpen, setAdvancedSearchOpen] = useState(false);
  const [isAdvancedSearching, setIsAdvancedSearching] = useState(false);
  const [listLastKey, setListLastKey] = useState(null);
  const listLastKeyRef = useRef(null);
  useEffect(() => {
    listLastKeyRef.current = listLastKey;
  }, [listLastKey]);
  const [storageUsedBytes, setStorageUsedBytes] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [toast, setToast] = useState(null);

  const [signatureChecklistOpen, setSignatureChecklistOpen] = useState(false);
  const [signatureChecklistContract, setSignatureChecklistContract] = useState(null);
  const [signModalOpen, setSignModalOpen] = useState(false);
  const [signJobId, setSignJobId] = useState(null);
  const [signDocTitle, setSignDocTitle] = useState('');
  const [signDocStatus, setSignDocStatus] = useState('');
  const [search, setSearch] = useState('');
  const [sortKey, setSortKey] = useState(null);
  const [sortDir, setSortDir] = useState('asc');

  const [signatureCancelConfirmOpen, setSignatureCancelConfirmOpen] = useState(false);
  const [pendingCancelDocument, setPendingCancelDocument] = useState(null);
  const [isCancelingSignature, setIsCancelingSignature] = useState(false);
  const [signatureStatusOpen, setSignatureStatusOpen] = useState(false);
  const [signatureStatusDocument, setSignatureStatusDocument] = useState(null);

  const [viewerOpen, setViewerOpen] = useState(false);
  const [viewerJobId, setViewerJobId] = useState(null);
  const [viewerTitle, setViewerTitle] = useState('');

  const dismissToast = useCallback(() => setToast(null), []);

  const showToast = useCallback((type, message) => {
    setToast({ type, message: String(message || 'Ocorreu um erro.') });
  }, []);

  const loadDocuments = useCallback(
    async (loadMore = false) => {
      if (loadMore) {
        setIsLoadingMore(true);
      } else {
        setIsLoading(true);
      }

      try {
        const result = await listUploadedSignatureDocumentsPage(
          loadMore ? listLastKeyRef.current : null,
          { pageSize: LIST_PAGE_SIZE }
        );

        if (!result.success) {
          showToast('error', result.error);
          if (!loadMore) {
            setDocuments([]);
            setStorageUsedBytes(0);
            setListLastKey(null);
          }
          return;
        }

        const data = result.data || {};
        const pageItems = Array.isArray(data.items) ? data.items : [];

        if (loadMore) {
          setDocuments((prev) => mergeUploadedDocumentsByJobId(prev, pageItems));
        } else {
          setDocuments(pageItems);
          setStorageUsedBytes(Number(data.storage_used_bytes) || 0);
        }

        setListLastKey(data.lastEvaluatedKey || null);
      } catch (error) {
        console.error('loadDocuments:', error);
        showToast('error', 'Erro ao carregar documentos.');
      } finally {
        if (loadMore) {
          setIsLoadingMore(false);
        } else {
          setIsLoading(false);
        }
      }
    },
    [showToast]
  );

  const activeDocuments = listMode === 'search' ? searchDocuments : documents;

  const handleAdvancedSearch = useCallback(
    async (criteria) => {
      setIsAdvancedSearching(true);
      try {
        const result = await contractService.searchAllDocuments({
          ...criteria,
          source: 'upload',
        });
        if (!result.success) {
          showToast('error', result.error);
          return;
        }
        const items = result.data.items || [];
        setSearchDocuments(items);
        setSearchTruncated(Boolean(result.data.truncated));
        setListMode('search');
        setAdvancedSearchOpen(false);
        if (items.length === 0) {
          showToast('info', 'Nenhum PDF encontrado para os filtros informados.');
        } else if (result.data.truncated) {
          showToast(
            'info',
            `Mostrando os primeiros ${DOCUMENT_SEARCH_MAX_RESULTS} resultados. Refine os filtros.`
          );
        }
      } catch (error) {
        console.error('handleAdvancedSearch:', error);
        showToast('error', 'Erro na busca avançada.');
      } finally {
        setIsAdvancedSearching(false);
      }
    },
    [showToast]
  );

  const handleExitAdvancedSearch = useCallback(() => {
    setListMode('browse');
    setSearchDocuments([]);
    setSearchTruncated(false);
  }, []);

  const handleLoadMore = useCallback(async () => {
    if (!listLastKey || isLoadingMore) return;
    await loadDocuments(true);
  }, [listLastKey, isLoadingMore, loadDocuments]);

  useEffect(() => {
    loadDocuments(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- initial load only
  }, []);

  const handleUpload = async (files) => {
    setIsUploading(true);
    try {
      const result = await uploadUploadedSignaturePdfs(files);
      if (!result.success) {
        showToast('error', result.error);
        return false;
      }
      const uploaded = result.data?.items?.length ?? 0;
      const partialErrors = result.data?.errors;
      if (partialErrors?.length) {
        showToast(
          'info',
          `${uploaded} arquivo(s) enviado(s). Alguns arquivos falharam.`
        );
      } else {
        showToast('success', `${uploaded || files.length} PDF(s) enviado(s) com sucesso.`);
      }
      await loadDocuments(false);
      return true;
    } finally {
      setIsUploading(false);
    }
  };

  const handleDownload = async (doc) => {
    const jobId = doc?.job_id;
    if (!jobId) return;
    const result = await downloadUploadedSignaturePdf(jobId);
    if (!result.success) {
      showToast('error', result.error);
      return;
    }
    const { blob, fileName } = result.data;
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = fileName;
    anchor.click();
    URL.revokeObjectURL(url);
  };

  const handleView = (doc) => {
    if (!doc?.job_id) return;
    setViewerJobId(doc.job_id);
    setViewerTitle(doc.title || '');
    setViewerOpen(true);
  };

  const handleCloseViewer = useCallback(() => {
    setViewerOpen(false);
    setViewerJobId(null);
    setViewerTitle('');
  }, []);

  const viewerLoadPdf = useCallback(async () => {
    if (!viewerJobId) {
      return { success: false, error: 'Documento inválido.' };
    }
    return downloadUploadedSignaturePdf(viewerJobId);
  }, [viewerJobId]);

  const handleSign = (doc) => {
    if (!doc?.job_id) return;
    const status = normalizeDocumentStatus(doc.status);
    if (status === 'PENDING_SIGNATURE' || status === 'SIGNED') {
      showToast('error', 'Este documento já foi enviado para assinatura ou já está assinado.');
      return;
    }
    if (status !== 'PDF_UPLOADED' && status !== 'PDF_GENERATED') {
      showToast('error', 'Só é possível assinar quando o PDF está pronto para envio.');
      return;
    }
    setSignatureChecklistContract({
      job_id: doc.job_id,
      name: doc.title || 'Documento',
      title: doc.title,
      status,
      is_manual_signature: true,
    });
    setSignatureChecklistOpen(true);
  };

  const handleCloseSignatureChecklist = useCallback(() => {
    setSignatureChecklistOpen(false);
    setSignatureChecklistContract(null);
  }, []);

  const handleContinueSignatureChecklist = useCallback(() => {
    const contract = signatureChecklistContract;
    setSignatureChecklistOpen(false);
    setSignatureChecklistContract(null);
    if (!contract?.job_id) return;
    setSignJobId(contract.job_id);
    setSignDocTitle(contract.title || contract.name || '');
    setSignDocStatus(contract.status || 'PDF_UPLOADED');
    setSignModalOpen(true);
  }, [signatureChecklistContract]);

  const handleRequestCancelSignature = useCallback((doc) => {
    if (!doc?.job_id) {
      showToast('error', 'Não foi possível identificar o documento.');
      return;
    }
    if (normalizeDocumentStatus(doc.status) !== 'PENDING_SIGNATURE') {
      showToast(
        'error',
        'Só é possível cancelar documentos aguardando assinatura. Documentos já assinados não podem ser revertidos por aqui.'
      );
      return;
    }
    setPendingCancelDocument(doc);
    setSignatureCancelConfirmOpen(true);
  }, [showToast]);

  const handleViewSignatureStatus = useCallback((doc) => {
    if (!doc?.job_id) {
      showToast('error', 'Não foi possível identificar o documento.');
      return;
    }
    setSignatureStatusDocument(doc);
    setSignatureStatusOpen(true);
  }, [showToast]);

  const handleCloseCancelSignatureConfirm = useCallback(() => {
    setSignatureCancelConfirmOpen(false);
    setPendingCancelDocument(null);
  }, []);

  const handleConfirmCancelSignature = useCallback(async () => {
    const doc = pendingCancelDocument;
    setSignatureCancelConfirmOpen(false);
    setPendingCancelDocument(null);

    const jobId = doc?.job_id;
    if (!jobId) return;

    setIsCancelingSignature(true);
    try {
      const result = await cancelSignatureJob(String(jobId));
      if (result.ok) {
        showToast('success', 'Envio para assinatura cancelado com sucesso.');
        await loadDocuments(false);
        return;
      }
      showToast(
        'error',
        result.messagePt || 'Não foi possível cancelar o envio para assinatura.'
      );
    } catch (err) {
      console.error('handleConfirmCancelSignature:', err);
      showToast(
        'error',
        formatApiErrorMessage(err, 'Erro ao cancelar envio para assinatura.')
      );
    } finally {
      setIsCancelingSignature(false);
    }
  }, [pendingCancelDocument, loadDocuments, showToast]);

  const handleDelete = async (doc) => {
    const jobId = doc?.job_id;
    if (!jobId) return;
    const confirmed = window.confirm(
      `Excluir "${doc.title || 'documento'}"? O arquivo será removido do armazenamento.`
    );
    if (!confirmed) return;

    setIsDeleting(true);
    try {
      const result = await deleteUploadedSignatureDocument(jobId);
      if (!result.success) {
        showToast('error', result.error);
        return;
      }
      showToast('success', 'Documento excluído.');
      await loadDocuments(false);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleSortColumn = useCallback((key) => {
    if (sortKey === key) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDir(key === 'name' ? 'asc' : 'desc');
    }
  }, [sortKey]);

  const sortedDocuments = useMemo(() => {
    const list = [...activeDocuments];
    if (sortKey === 'name' || sortKey === 'size') {
      list.sort((a, b) => compareUploadedDocuments(a, b, sortKey, sortDir));
      return list;
    }
    list.sort((a, b) => compareUploadedDocuments(a, b, null, 'desc'));
    return list;
  }, [activeDocuments, sortKey, sortDir]);

  const filteredDocuments = useMemo(() => {
    if (listMode === 'search') {
      return sortedDocuments;
    }
    const term = search.trim().toLowerCase();
    if (!term) return sortedDocuments;
    return sortedDocuments.filter((doc) =>
      (doc.title || '').toLowerCase().includes(term)
    );
  }, [sortedDocuments, search, listMode]);

  const actionsDisabled = isUploading || isDeleting || isCancelingSignature;

  return (
    <>
      <Helmet>
        <title>Upload Documentos | Neo Modelos</title>
      </Helmet>

      <div className={PAGE_CONTENT_CLASS}>
        <header className="mb-6 md:mb-8">
          <h1 className="text-2xl md:text-3xl font-bold text-foreground font-heading tracking-tight">
            Upload Documentos
          </h1>
          <p className="text-sm text-muted-foreground mt-2 font-body max-w-2xl">
            Envie PDFs prontos para posicionar campos de assinatura e enviar pelo fluxo Assinafy,
            com os mesmos limites de licença dos demais documentos.
          </p>
        </header>

        <div className="grid grid-cols-1 gap-6 lg:gap-8">
          <UploadDropZone
            onUpload={handleUpload}
            uploading={isUploading}
            storageUsedBytes={storageUsedBytes}
            storageLimitBytes={MAX_UPLOADED_STORAGE_BYTES}
          />

          <section className="bg-card border border-border rounded-xl shadow-elevation-sm overflow-hidden">
            <div className="px-4 md:px-6 py-4 border-b border-border flex items-center justify-between gap-3">
              <h2 className="text-base font-semibold text-foreground font-heading">
                Seus PDFs enviados
              </h2>
              <div className="flex flex-wrap items-center gap-2">
                <Button
                  type="button"
                  variant="default"
                  size="sm"
                  iconName="Search"
                  onClick={() => setAdvancedSearchOpen(true)}
                >
                  Busca avançada
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  iconName="RefreshCw"
                  onClick={() => {
                    if (listMode === 'search') {
                      handleExitAdvancedSearch();
                    }
                    loadDocuments(false);
                  }}
                  disabled={isLoading || isLoadingMore}
                >
                  Atualizar
                </Button>
              </div>
            </div>

            {listMode === 'search' ? (
              <div className="mx-4 md:mx-6 mb-4 flex flex-col sm:flex-row sm:items-center gap-3 rounded-lg border border-primary/25 bg-primary/5 px-4 py-3 text-sm">
                <p className="text-muted-foreground flex-1">
                  Resultados da busca: {searchDocuments.length} PDF(s)
                  {searchTruncated
                    ? ` (limite de ${DOCUMENT_SEARCH_MAX_RESULTS})`
                    : ''}
                  .
                </p>
                <Button type="button" variant="outline" size="sm" onClick={handleExitAdvancedSearch}>
                  Voltar à lista recente
                </Button>
              </div>
            ) : null}

            {!isLoading && activeDocuments.length > 0 && listMode === 'browse' ? (
              <UploadedDocumentFilters search={search} onSearchChange={setSearch} />
            ) : null}

            {isLoading ? (
              <div className="flex justify-center py-16">
                <LoadingMark message="Carregando lista…" />
              </div>
            ) : activeDocuments.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-16 px-4 text-center gap-3">
                <Icon name="Inbox" size={40} className="text-muted-foreground/70" />
                <p className="text-sm text-muted-foreground font-body">
                  Nenhum PDF enviado ainda. Use a área acima para fazer upload.
                </p>
              </div>
            ) : filteredDocuments.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-16 px-4 text-center gap-3">
                <Icon name="Search" size={40} className="text-muted-foreground/70" />
                <p className="text-sm text-muted-foreground font-body">
                  Nenhum documento encontrado para &quot;{search.trim()}&quot;.
                </p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full table-fixed">
                  <thead className="bg-muted/50 border-b border-border">
                    <tr>
                      <th className="px-4 md:px-6 py-3 text-left text-xs font-semibold text-foreground uppercase tracking-wider font-caption w-auto">
                        <UploadedDocumentSortHeader
                          label="Documento"
                          columnKey="name"
                          sortKey={sortKey}
                          sortDir={sortDir}
                          onSortColumn={handleSortColumn}
                        />
                      </th>
                      <th className="px-4 md:px-6 py-3 text-left text-xs font-semibold text-foreground uppercase tracking-wider font-caption hidden md:table-cell md:w-[7rem]">
                        <UploadedDocumentSortHeader
                          label="Tamanho"
                          columnKey="size"
                          sortKey={sortKey}
                          sortDir={sortDir}
                          onSortColumn={handleSortColumn}
                        />
                      </th>
                      <th className="px-4 md:px-6 py-3 text-left text-xs font-semibold text-foreground uppercase tracking-wider font-caption hidden md:table-cell md:w-[9rem]">
                        Status
                      </th>
                      <th className="px-4 md:px-6 py-3 text-left text-xs font-semibold text-foreground uppercase tracking-wider font-caption hidden lg:table-cell lg:w-[10rem]">
                        Criação
                      </th>
                      <th className="px-4 md:px-6 py-3 text-right text-xs font-semibold text-foreground uppercase tracking-wider font-caption w-[9.5rem] md:w-[11rem]">
                        Ações
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {filteredDocuments.map((doc) => (
                        <UploadedDocumentTableRow
                          key={doc.job_id}
                          document={doc}
                          onDownload={handleDownload}
                          onView={handleView}
                          onSign={handleSign}
                          onDelete={handleDelete}
                          onCancelSignature={handleRequestCancelSignature}
                          onViewSignatureStatus={handleViewSignatureStatus}
                          actionsDisabled={actionsDisabled}
                        />
                      ))}
                  </tbody>
                </table>
              </div>
            )}

            {!isLoading && listMode === 'browse' && listLastKey ? (
              <div className="flex justify-center border-t border-border px-4 py-6">
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleLoadMore}
                  disabled={isLoadingMore}
                  className="min-w-[140px]"
                >
                  {isLoadingMore ? 'Carregando…' : 'Ver mais'}
                </Button>
              </div>
            ) : null}
          </section>
        </div>
      </div>

      {isUploading || isDeleting || isCancelingSignature ? (
        <LoadingOverlay message="Processando…" />
      ) : null}

      {toast ? (
        <Toast type={toast.type} message={toast.message} onDismiss={dismissToast} />
      ) : null}

      <AdvancedDocumentSearchModal
        open={advancedSearchOpen}
        onClose={() => setAdvancedSearchOpen(false)}
        onSearch={handleAdvancedSearch}
        searching={isAdvancedSearching}
        lockSource="upload"
        hideTypeFilter
      />

      <SignatureSendChecklistModal
        open={signatureChecklistOpen}
        contract={signatureChecklistContract}
        onClose={handleCloseSignatureChecklist}
        onContinue={handleContinueSignatureChecklist}
      />

      <SignatureFieldEditorModal
        open={signModalOpen}
        jobId={signJobId}
        documentTitle={signDocTitle}
        documentStatus={signDocStatus}
        onClose={() => {
          setSignModalOpen(false);
          setSignJobId(null);
        }}
        onSentToSignature={() => {
          setSignModalOpen(false);
          setSignJobId(null);
          loadDocuments(false);
          showToast('success', 'Documento enviado para assinatura.');
        }}
      />

      <SignatureCancelConfirmDialog
        open={signatureCancelConfirmOpen}
        documentTitle={pendingCancelDocument?.title || ''}
        onCancel={handleCloseCancelSignatureConfirm}
        onConfirm={handleConfirmCancelSignature}
      />

      <SignatureStatusModal
        open={signatureStatusOpen}
        document={signatureStatusDocument}
        onClose={() => {
          setSignatureStatusOpen(false);
          setSignatureStatusDocument(null);
        }}
      />

      <UploadedDocumentPdfViewerModal
        open={viewerOpen}
        loadPdf={viewerLoadPdf}
        documentTitle={viewerTitle}
        onClose={handleCloseViewer}
      />
    </>
  );
};

export default UploadedDocumentsPage;
